/**
 * Deduplicação de eventos do Sentry — lógica PURA, sem SDK.
 *
 * Existe por causa de um número: **563 `console.error` em `src/`**. Ligar o
 * `captureConsoleIntegration` sem filtro transforma cada um deles em evento, e
 * um `console.error` dentro de um laço (retry de rede, render em lista, polling)
 * vira mil eventos idênticos em segundos. O resultado não é observabilidade —
 * é quota queimada e o sinal afogado, que é o oposto do objetivo.
 *
 * A regra é simples e deliberadamente burra: **a mesma mensagem só passa uma
 * vez por janela.** Não agrupa por stack, não tenta ser esperto — mensagem
 * igual dentro da janela é ruído por definição, e o Sentry já agrega
 * ocorrências do mesmo issue do lado dele.
 *
 * O que NÃO faz: silenciar. O primeiro de cada mensagem sempre passa. Um erro
 * novo nunca é engolido — só a repetição é.
 */

/** Janela de silêncio por mensagem. */
export const DEDUPE_WINDOW_MS = 60_000;

/**
 * Teto de mensagens distintas lembradas. Sem ele o mapa cresce sem limite numa
 * sessão longa (o app é uma SPA que fica aberta) — e um vazamento de memória
 * introduzido pela telemetria seria uma ironia cara.
 */
export const DEDUPE_MAX_KEYS = 200;

export interface DedupeState {
  seen: Map<string, number>;
}

export function createDedupeState(): DedupeState {
  return { seen: new Map() };
}

/**
 * Normaliza a mensagem para a chave.
 *
 * Corta em 200 chars e remove o que muda a cada ocorrência sem mudar o
 * problema: uuids, timestamps e números longos. Sem isso, "falha no anexo
 * abc-123" e "falha no anexo def-456" contam como mensagens diferentes e a
 * dedupe não pega nada — que é o modo de falha silencioso desta função.
 */
export function dedupeKey(message: string): string {
  return (message || "")
    .slice(0, 200)
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "<uuid>")
    .replace(/\d{4}-\d{2}-\d{2}T[\d:.]+Z?/g, "<ts>")
    // Sem \b nas pontas de propósito: `\b\d{4,}\b` NÃO casa em "20000ms",
    // porque a fronteira entre "0" e "m" não existe (os dois são \w). Era o
    // caso mais comum de todos — mensagem de timeout — e passava batido.
    .replace(/\d{4,}/g, "<n>")
    .trim();
}

/**
 * Deixa passar?
 *
 * `now` é parâmetro, não `Date.now()` interno — é o que torna a janela
 * testável sem timer nem mock global.
 */
export function shouldSend(state: DedupeState, message: string, now: number): boolean {
  const key = dedupeKey(message);
  if (!key) return true; // sem mensagem não há como deduplicar; não engole

  const last = state.seen.get(key);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return false;

  // Poda preguiçosa: só quando o mapa encosta no teto, e só o que já expirou.
  // Se nada expirou, remove o mais antigo — a alternativa (parar de deduplicar)
  // devolveria o problema que esta função existe para resolver.
  if (state.seen.size >= DEDUPE_MAX_KEYS) {
    let oldestKey: string | null = null;
    let oldestAt = Infinity;
    for (const [k, t] of state.seen) {
      if (now - t >= DEDUPE_WINDOW_MS) {
        state.seen.delete(k);
        continue;
      }
      if (t < oldestAt) {
        oldestAt = t;
        oldestKey = k;
      }
    }
    if (state.seen.size >= DEDUPE_MAX_KEYS && oldestKey) state.seen.delete(oldestKey);
  }

  state.seen.set(key, now);
  return true;
}
